/*
 * <fleet-alerts-page> — the Alerts section, built on base-ui's BasePage (the ONE
 * page primitive: back · title [+ icon] · description · badges/actions row ·
 * full-height content). Reusing BasePage gives the Alerts view the same chrome as
 * every other detail page for free.
 *
 * The view renders the fleet audit log newest-first: runs of identical consecutive
 * alerts collapse into one expandable row so a flood reads as a single entry, each
 * row carries its severity colour + kind icon, and clicking a row body opens the
 * gateway it belongs to. The log is append-only by design — it is the record of what
 * everyone did, including admins — so the page reads and never mutates it.
 */
import { html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
import { statusBadge, groupConsecutive, type ConsecutiveGroup } from '@vitesse/base-ui';
import { ft } from '../core/i18n';
import { relativeTime } from '../core/fleet-model';
import type { AlertItem } from '../core/alerts-manager';

const KIND_ICON: Record<string, string> = {
  online: 'check',
  offline: 'unplug',
  claimed: 'shield-check',
  unclaimed: 'hard-drive',
  revoked: 'shield-alert',
  removed: 'trash-2',
  enrolled: 'hard-drive',
  version: 'package',
  ids_alert: 'shield-alert',
  ids_block: 'shield-alert',
};
const SEV_CLASS: Record<string, string> = { critical: 'red', warning: 'amber', info: 'blue' };

@customElement('fleet-alerts-page')
export class FleetAlertsPage extends BasePage {
  @property({ attribute: false }) items: AlertItem[] = [];
  @property() error = '';

  /** Group heads (keyed by the head event id) the user has expanded — runs of
   *  identical consecutive alerts collapse into one row by default. */
  @state() private expandedGroups = new Set<string>();

  /** Identity of an alert for consecutive grouping — title + detail + gateway, but
   *  NOT the timestamp (which differs per occurrence, so identical events at
   *  different times still collapse). Mirrors the bell's grouping. */
  private static key(a: AlertItem): string {
    return `${a.title}\0${a.detail ?? ''}\0${a.gatewayId}`;
  }

  protected pageTitle(): string {
    return ft('view.alertsTitle');
  }
  protected titleIcon(): string | null {
    return null; // no bell on the page title — the header already has the notification bell
  }
  protected backLabel(): string {
    return ft('tree.overview');
  }
  protected onBack(): void {
    // Top-level section → "back" returns to the fleet overview, reusing the shell's
    // node-select nav handler (the same event the nav tree dispatches).
    this.dispatchEvent(
      new CustomEvent('node-select', { detail: { key: 'overview' }, bubbles: true, composed: true }),
    );
  }

  protected renderBadges(): unknown {
    const crit = this.items.filter((a) => a.severity === 'critical').length;
    const warn = this.items.filter((a) => a.severity === 'warning').length;
    // Real status badges (base-ui statusBadge — the same primitive the gateway
    // cards/panels use): severity-coloured when non-zero, muted grey at zero so a
    // healthy fleet doesn't show an alarming red "0 critical".
    return html`${statusBadge(crit > 0 ? 'red' : 'gray', ft('alerts.critical', { n: crit }))}${statusBadge(
      warn > 0 ? 'yellow' : 'gray',
      ft('alerts.warnings', { n: warn }),
    )}`;
  }

  protected renderContent(): TemplateResult {
    if (this.items.length === 0) {
      return html`<div class="fleet-card fleet-empty">
        <vitesse-icon name="check" size="20"></vitesse-icon> ${this.error || ft('view.noAlerts')}
      </div>`;
    }
    // Collapse runs of identical consecutive alerts into one expandable row.
    const rows: TemplateResult[] = [];
    for (const g of groupConsecutive(this.items, FleetAlertsPage.key)) {
      rows.push(g.count === 1 ? this.renderRow(g.head) : this.renderGroup(g));
    }
    return html`
      <div class="fleet-alerts-count-head">
        <span class="fleet-alerts-count">${ft('alerts.nTotal', { n: this.items.length })}</span>
      </div>
      <div class="base-roster-list base-roster-list--page">${rows}</div>
    `;
  }

  /** A collapsed run of identical consecutive alerts: one header row with a count +
   *  chevron, expanding to the individual members (each a normal row). */
  private renderGroup(g: ConsecutiveGroup<AlertItem>): TemplateResult {
    const a = g.head;
    const open = this.expandedGroups.has(a.id);
    const meta = [a.detail, a.gatewayId ? `${ft('dialog.gateway')} ${a.gatewayId.slice(0, 8)}` : '', relativeTime(a.ts)]
      .filter(Boolean)
      .join(' · ');
    return html`
      <div class="fleet-alert-group">
        <div class="base-roster-row fleet-alert fleet-alert-head">
          <button
            class="fleet-alert-chevron ${open ? 'expanded' : ''}"
            aria-label=${open ? 'Collapse' : 'Expand'}
            @click=${() => this.toggleGroup(a.id)}
          >
            <vitesse-icon name="chevron-right" size="16"></vitesse-icon>
          </button>
          <span class="fleet-alert-icon fleet-alert-icon--${SEV_CLASS[a.severity] ?? 'blue'}">
            <vitesse-icon name=${KIND_ICON[a.kind] ?? 'bell'} size="15"></vitesse-icon>
          </span>
          <div class="fleet-alert-text" role="button" tabindex="0" @click=${() => this.toggleGroup(a.id)}>
            <div class="fleet-alert-title">${a.title}<span class="fleet-alert-count">×${g.count}</span></div>
            <div class="fleet-alert-body">${meta}</div>
          </div>
        </div>
        <div class="fleet-alert-children" style="grid-template-rows:${open ? '1fr' : '0fr'}">
          <div class="fleet-alert-children-overflow">
            ${open ? g.items.map((m) => this.renderRow(m)) : nothing}
          </div>
        </div>
      </div>
    `;
  }

  private toggleGroup(headId: string): void {
    const next = new Set(this.expandedGroups);
    next.has(headId) ? next.delete(headId) : next.add(headId);
    this.expandedGroups = next;
  }

  private renderRow(a: AlertItem): TemplateResult {
    const meta = [a.detail, a.gatewayId ? `${ft('dialog.gateway')} ${a.gatewayId.slice(0, 8)}` : '', relativeTime(a.ts)]
      .filter(Boolean)
      .join(' · ');
    return html`<div class="base-roster-row fleet-alert">
      <span class="fleet-alert-icon fleet-alert-icon--${SEV_CLASS[a.severity] ?? 'blue'}">
        <vitesse-icon name=${KIND_ICON[a.kind] ?? 'bell'} size="15"></vitesse-icon>
      </span>
      <div
        class="fleet-alert-text"
        role=${a.gatewayId ? 'button' : nothing}
        tabindex=${a.gatewayId ? '0' : nothing}
        @click=${() => a.gatewayId && this.openGateway(a.gatewayId)}
      >
        <div class="fleet-alert-title">${a.title}</div>
        <div class="fleet-alert-body">${meta}</div>
      </div>
    </div>`;
  }

  private openGateway(fingerprint: string): void {
    this.dispatchEvent(new CustomEvent('gateway-open', { detail: { fingerprint }, bubbles: true, composed: true }));
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fleet-alerts-page': FleetAlertsPage;
  }
}
